import { createApi } from '@reduxjs/toolkit/query/react';
import { RefreshDiffResponse, RefreshType, TemplateHistory } from '@features/survey-templates/types';
import { ApiResponse } from '../types/api';
import { axiosBaseQuery } from './axiosQuery';

interface BulkSurveysUpdateMutationParams {
  historyId: string;
  templateId: string;
}

interface BulkSurveysRefreshMutationParams extends BulkSurveysUpdateMutationParams {
  refreshTypes: RefreshType[];
}

export const surveyTemplatesHistoryApi = createApi({
  reducerPath: 'surveyTemplatesHistoryApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['survey-templates-history'],
  endpoints: (builder) => ({
    getHistory: builder.query<TemplateHistory[], string>({
      transformResponse: (response: ApiResponse<TemplateHistory[]>) => response.data,
      query: (templateId) => ({
        url: `/survey-templates/${templateId}/history`,
        method: 'get',
      }),
      providesTags: ['survey-templates-history'],
    }),
    getHistoryById: builder.query<TemplateHistory, { templateId: string; historyId: string }>({
      transformResponse: (response: ApiResponse<TemplateHistory>) => response.data,
      query: ({ templateId, historyId }) => ({
        url: `/survey-templates/${templateId}/history/${historyId}`,
        method: 'get',
      }),
      providesTags: ['survey-templates-history'],
    }),
    bulkSurveysRefresh: builder.mutation<ApiResponse<{ jobId: string }>, BulkSurveysRefreshMutationParams>({
      invalidatesTags: ['survey-templates-history'],
      query: ({ templateId, historyId, refreshTypes }) => ({
        url: `/survey-templates/${templateId}/history/${historyId}/refresh`,
        method: 'post',
        data: { refreshTypes },
      }),
    }),
    getRefreshDiff: builder.query<
      RefreshDiffResponse,
      { templateId: string; historyId: string; refreshType: RefreshType }
    >({
      transformResponse: (response: ApiResponse<RefreshDiffResponse>) => response.data,
      query: ({ templateId, historyId, refreshType }) => ({
        url: `/survey-templates/${templateId}/history/${historyId}/refresh-diff`,
        method: 'get',
        params: { refreshType },
      }),
    }),
    bulkSurveysRefreshStatus: builder.query<{ isRunning: boolean }, { templateId: string; historyId: string }>({
      transformResponse: (response: ApiResponse<{ isRunning: boolean }>) => response.data,
      query: ({ templateId, historyId }) => ({
        url: `/survey-templates/${templateId}/history/${historyId}/active-refresh-job`,
        method: 'get',
      }),
      providesTags: ['survey-templates-history'],
    }),
    bulkSurveysDelete: builder.mutation<ApiResponse<TemplateHistory>, BulkSurveysUpdateMutationParams>({
      invalidatesTags: ['survey-templates-history'],
      query: ({ templateId, historyId }) => ({
        url: `/survey-templates/${templateId}/history/${historyId}`,
        method: 'delete',
      }),
    }),
  }),
});

export const {
  useGetHistoryQuery,
  useGetHistoryByIdQuery,
  useBulkSurveysRefreshMutation,
  useBulkSurveysDeleteMutation,
  useBulkSurveysRefreshStatusQuery,
  useLazyGetRefreshDiffQuery,
} = surveyTemplatesHistoryApi;
